import { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import Button from '../ui/Button';

/**
 * The ways to reach a person that exist in every build.
 *
 * The phone and the email are not a stand-in for a voice assistant: they are the
 * paths that survive it, so they are rendered first and unconditionally, and a
 * voice assistant is added beside them rather than instead of them. The voice
 * action renders only when a parent passes `onVoiceAssist` -- nothing does today,
 * so the slot renders nothing at all.
 *
 * Presentational and tenant-agnostic: the display values and the hrefs arrive as
 * props, read from businessInfo by the caller, so no number, address or tenant
 * name is written into this component. A `null` href means the value is still a
 * placeholder upstream; the modal then renders plain text, because a placeholder
 * must never become a dead `tel:` or `mailto:` link.
 *
 * Focus handling follows AssistantWidget and MobileNav: the close button takes
 * focus on open (it is first in the DOM, so the tab order starts at the way out),
 * Tab is trapped inside the dialog, Escape closes it, and the caller hands focus
 * back to whatever opened it.
 */

/** The same set the widget traps, so Tab behaves identically in both dialogs. */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const ContactModal = ({
  phone,
  phoneHref,
  email,
  emailHref,
  onVoiceAssist,
  onClose,
}) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = dialogRef.current?.querySelectorAll(FOCUSABLE_SELECTOR);

      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Move focus in even for a pointer click, so the tab order starts inside the
    // dialog. The close button is focused rather than the first action: the way
    // out is what a visitor needs first if this was not what they wanted.
    const focusTimer = window.setTimeout(() => {
      dialogRef.current?.querySelector('.contact-modal__close')?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.clearTimeout(focusTimer);
    };
  }, [onClose]);

  return (
    <>
      <div className="contact-modal__scrim" onClick={onClose} aria-hidden="true" />

      <div
        className="contact-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-modal-heading"
        ref={dialogRef}
      >
        <div className="contact-modal__header">
          <h3 className="contact-modal__heading" id="contact-modal-heading">
            Want to talk to a person?
          </h3>
          <button
            type="button"
            className="contact-modal__close"
            onClick={onClose}
            aria-label="Close the contact options"
          >
            <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
          </button>
        </div>

        <div className="contact-modal__actions">
          {phoneHref ? (
            <Button
              href={phoneHref}
              variant="primary"
              size="lg"
              fullWidth
              className="contact-modal__primary"
            >
              Call {phone}
            </Button>
          ) : (
            <p className="contact-modal__fallback text-small text-muted">
              Call {phone}
            </p>
          )}

          {emailHref ? (
            <Button
              href={emailHref}
              variant="secondary"
              size="lg"
              fullWidth
              className="contact-modal__secondary"
            >
              {email}
            </Button>
          ) : (
            <p className="contact-modal__fallback text-small text-muted">{email}</p>
          )}

          {/* Reserved for the voice assistant. Nothing renders until a parent
              passes onVoiceAssist, and when one does this modal does not change
              otherwise: the phone and the email stay exactly where they are. */}
          {onVoiceAssist ? (
            <Button
              variant="ghost"
              size="lg"
              fullWidth
              className="contact-modal__voice"
              onClick={onVoiceAssist}
            >
              Talk to our AI voice assistant
            </Button>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default ContactModal;
